import React, { useState, useEffect, useCallback } from 'react';
import { HeaderFooterConfig, HeaderFooterColumn, SystemVariable, ContractBlock, Variable as VariableType } from '../types';
import { systemVariablesApi } from '../services/api';
import { 
  FileText, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  ChevronDown,
  ChevronUp,
  Variable,
  Layers
} from 'lucide-react';

interface BlockVariable {
  id: string;
  name: string;
  label: string;
  blockName: string;
  placeholder: string;
}

interface HeaderFooterEditorProps {
  type: 'header' | 'footer';
  config: HeaderFooterConfig | null;
  onChange: (config: HeaderFooterConfig) => void;
  contractId?: string; // Für Vorschau
  contractBlocks?: ContractBlock[]; // Block-Variablen verwenden
}

const defaultConfig: HeaderFooterConfig = {
  enabled: false,
  columns: 1,
  height: 15,
  firstPageDifferent: false,
  leftColumn: { content: '', alignment: 'left' },
  centerColumn: { content: '', alignment: 'center' },
  rightColumn: { content: '', alignment: 'right' }
};

export default function HeaderFooterEditor({ type, config, onChange, contractId, contractBlocks }: HeaderFooterEditorProps) {
  const [localConfig, setLocalConfig] = useState<HeaderFooterConfig>(config || defaultConfig);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showVariableSearch, setShowVariableSearch] = useState<{
    field: 'left' | 'center' | 'right';
    isFirstPage?: boolean;
  } | null>(null);
  const [variableSearch, setVariableSearch] = useState('');
  const [variables, setVariables] = useState<SystemVariable[]>([]);
  const [variableCategories, setVariableCategories] = useState<{ category: string; variables: SystemVariable[] }[]>([]);
  const [blockVariables, setBlockVariables] = useState<BlockVariable[]>([]);

  useEffect(() => {
    loadVariables();
  }, []);

  useEffect(() => {
    if (config) {
      setLocalConfig(config);
    }
  }, [config]);

  // Block-Variablen extrahieren
  useEffect(() => {
    if (contractBlocks && contractBlocks.length > 0) {
      const vars: BlockVariable[] = [];
      
      contractBlocks.forEach((cb) => {
        if (cb.block && cb.block.variables) {
          cb.block.variables.forEach((v: VariableType) => {
            vars.push({
              id: `block_${cb.id}_${v.id}`,
              name: v.name,
              label: v.label || v.name,
              blockName: cb.block!.name,
              placeholder: `<<${v.name}>>`
            });
          });
        }
      });
      
      setBlockVariables(vars);
    } else {
      setBlockVariables([]);
    }
  }, [contractBlocks]);

  const loadVariables = async () => {
    try {
      const { categories } = await systemVariablesApi.getGrouped();
      setVariableCategories(categories);
      
      // Flache Liste für die Suche
      const allVars = categories.flatMap((c: any) => c.variables);
      setVariables(allVars);
    } catch (err) {
      console.error('Failed to load variables:', err);
    }
  };

  const handleChange = useCallback((updates: Partial<HeaderFooterConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  }, [localConfig, onChange]);

  const updateColumn = (
    field: 'left' | 'center' | 'right', 
    updates: Partial<HeaderFooterColumn>,
    isFirstPage = false
  ) => {
    const columnKey = `${field}Column` as 'leftColumn' | 'centerColumn' | 'rightColumn';
    
    if (isFirstPage && localConfig.firstPageDifferent) {
      const firstPage = localConfig.firstPage || {
        leftColumn: { content: '', alignment: 'left' },
        centerColumn: { content: '', alignment: 'center' },
        rightColumn: { content: '', alignment: 'right' }
      };
      
      handleChange({
        firstPage: {
          ...firstPage,
          [columnKey]: { ...firstPage[columnKey], ...updates }
        }
      });
    } else {
      handleChange({
        [columnKey]: { ...localConfig[columnKey], ...updates }
      });
    }
  };

  const insertVariable = (variableId: string, isBlockVariable = false) => {
    if (!showVariableSearch) return;
    
    const { field, isFirstPage } = showVariableSearch;
    const columnKey = `${field}Column` as 'leftColumn' | 'centerColumn' | 'rightColumn';
    
    let currentContent = '';
    if (isFirstPage && localConfig.firstPageDifferent && localConfig.firstPage) {
      currentContent = localConfig.firstPage[columnKey]?.content || '';
    } else {
      currentContent = localConfig[columnKey]?.content || '';
    }

    // Block-Variablen verwenden <<variablenname>>, System-Variablen {{variablenId}}
    const insertText = isBlockVariable 
      ? `<<${variableId}>>`
      : `{{${variableId}}}`;

    updateColumn(field, { 
      content: currentContent + insertText
    }, isFirstPage);
    
    setShowVariableSearch(null);
    setVariableSearch('');
  };

  const filteredVariables = variableSearch
    ? variables.filter(v => 
        v.name.toLowerCase().includes(variableSearch.toLowerCase()) ||
        v.label.toLowerCase().includes(variableSearch.toLowerCase()) ||
        v.id.toLowerCase().includes(variableSearch.toLowerCase())
      )
    : [];

  const filteredBlockVariables = variableSearch
    ? blockVariables.filter(v => 
        v.name.toLowerCase().includes(variableSearch.toLowerCase()) ||
        v.label.toLowerCase().includes(variableSearch.toLowerCase()) ||
        v.blockName.toLowerCase().includes(variableSearch.toLowerCase())
      )
    : blockVariables;

  const renderColumnEditor = (
    field: 'left' | 'center' | 'right',
    label: string,
    isFirstPage = false
  ) => {
    const columnKey = `${field}Column` as 'leftColumn' | 'centerColumn' | 'rightColumn';
    const column = isFirstPage && localConfig.firstPageDifferent && localConfig.firstPage
      ? localConfig.firstPage[columnKey]
      : localConfig[columnKey];

    const shouldShow = 
      localConfig.columns === 3 ||
      (localConfig.columns === 2 && (field === 'left' || field === 'right')) ||
      (localConfig.columns === 1 && field === 'center');

    if (!shouldShow) return null;

    return (
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => updateColumn(field, { alignment: 'left' }, isFirstPage)}
              className={`p-1 rounded ${column?.alignment === 'left' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Links ausrichten"
            >
              <AlignLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => updateColumn(field, { alignment: 'center' }, isFirstPage)}
              className={`p-1 rounded ${column?.alignment === 'center' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Zentrieren"
            >
              <AlignCenter className="h-4 w-4" />
            </button>
            <button
              onClick={() => updateColumn(field, { alignment: 'right' }, isFirstPage)}
              className={`p-1 rounded ${column?.alignment === 'right' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Rechts ausrichten"
            >
              <AlignRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="relative">
          <textarea
            value={column?.content || ''}
            onChange={(e) => updateColumn(field, { content: e.target.value }, isFirstPage)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={2}
            placeholder="Text eingeben oder Variable einfügen..."
          />
          <button
            onClick={() => setShowVariableSearch({ field, isFirstPage })}
            className="absolute right-2 top-2 p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
            title="Variable einfügen"
          >
            <Variable className="h-4 w-4" />
          </button>
        </div>

        {/* Variable Search Dropdown */}
        {showVariableSearch?.field === field && showVariableSearch?.isFirstPage === isFirstPage && (
          <div className="absolute z-10 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={variableSearch}
                onChange={(e) => setVariableSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Variable suchen..."
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {variableSearch ? (
                <>
                  {/* Gefilterte Block-Variablen */}
                  {filteredBlockVariables.length > 0 && (
                    <div>
                      <div className="px-3 py-2 bg-amber-50 text-xs font-semibold text-amber-700 uppercase flex items-center gap-1">
                        <Layers className="h-3 w-3" />
                        Block-Variablen
                      </div>
                      {filteredBlockVariables.map((v) => (
                        <button
                          key={v.id}
                          onClick={() => insertVariable(v.name, true)}
                          className="w-full px-3 py-2 text-left hover:bg-amber-50 flex items-start gap-2"
                        >
                          <Layers className="h-4 w-4 text-amber-500 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{v.label}</div>
                            <div className="text-xs text-gray-500">
                              {v.blockName} · {v.placeholder}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Gefilterte System-Variablen */}
                  {filteredVariables.length > 0 && (
                    filteredVariables.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => insertVariable(v.id, false)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-start gap-2"
                      >
                        <Variable className="h-4 w-4 text-primary-500 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{v.name}</div>
                          <div className="text-xs text-gray-500">{v.example}</div>
                        </div>
                      </button>
                    ))
                  )}
                  {filteredBlockVariables.length === 0 && filteredVariables.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      Keine Variablen gefunden
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Block-Variablen (falls vorhanden) */}
                  {blockVariables.length > 0 && (
                    <div>
                      <div className="px-3 py-2 bg-amber-50 text-xs font-semibold text-amber-700 uppercase flex items-center gap-1">
                        <Layers className="h-3 w-3" />
                        Block-Variablen (aus hinzugefügten Bauteilen)
                      </div>
                      {blockVariables.map((v) => (
                        <button
                          key={v.id}
                          onClick={() => insertVariable(v.name, true)}
                          className="w-full px-3 py-2 text-left hover:bg-amber-50 flex items-start gap-2"
                        >
                          <Layers className="h-4 w-4 text-amber-500 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{v.label}</div>
                            <div className="text-xs text-gray-500">
                              {v.blockName} · {v.placeholder}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* System-Variablen nach Kategorien */}
                  {variableCategories.map((cat) => (
                    <div key={cat.category}>
                      <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                        {cat.category}
                      </div>
                      {cat.variables.map((v: any) => (
                        <button
                          key={v.id}
                          onClick={() => insertVariable(v.id, false)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-start gap-2"
                        >
                          <Variable className="h-4 w-4 text-primary-500 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{v.name}</div>
                            <div className="text-xs text-gray-500">{v.example}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="p-2 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowVariableSearch(null);
                  setVariableSearch('');
                }}
                className="w-full px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-400" />
          <span className="font-medium text-gray-700">
            {type === 'header' ? 'Kopfzeile' : 'Fußzeile'}
          </span>
          {localConfig.enabled && (
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
              Aktiv
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {type === 'header' ? 'Kopfzeile' : 'Fußzeile'} aktivieren
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localConfig.enabled}
                onChange={(e) => handleChange({ enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {localConfig.enabled && (
            <>
              {/* Column Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Spaltenanzahl
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((num) => (
                    <button
                      key={num}
                      onClick={() => handleChange({ columns: num as 1 | 2 | 3 })}
                      className={`flex-1 py-2 px-4 rounded-lg border ${
                        localConfig.columns === num
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {num} {num === 1 ? 'Spalte' : 'Spalten'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Height */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Höhe (mm)
                </label>
                <input
                  type="number"
                  value={localConfig.height}
                  onChange={(e) => handleChange({ height: parseInt(e.target.value) || 15 })}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min={5}
                  max={50}
                />
              </div>

              {/* First Page Different */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-700">
                  Erste Seite anders
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConfig.firstPageDifferent}
                    onChange={(e) => handleChange({ 
                      firstPageDifferent: e.target.checked,
                      firstPage: e.target.checked ? {
                        leftColumn: { content: '', alignment: 'left' },
                        centerColumn: { content: '', alignment: 'center' },
                        rightColumn: { content: '', alignment: 'right' }
                      } : undefined
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {/* Column Editors - Normal Pages */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  {localConfig.firstPageDifferent ? 'Folgeseiten' : 'Inhalt'}
                </h4>
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${localConfig.columns}, 1fr)` }}>
                  {localConfig.columns >= 2 && renderColumnEditor('left', 'Links')}
                  {localConfig.columns === 1 && renderColumnEditor('center', 'Mitte')}
                  {localConfig.columns === 3 && renderColumnEditor('center', 'Mitte')}
                  {localConfig.columns >= 2 && renderColumnEditor('right', 'Rechts')}
                </div>
              </div>

              {/* Column Editors - First Page */}
              {localConfig.firstPageDifferent && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Erste Seite</h4>
                  <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${localConfig.columns}, 1fr)` }}>
                    {localConfig.columns >= 2 && renderColumnEditor('left', 'Links', true)}
                    {localConfig.columns === 1 && renderColumnEditor('center', 'Mitte', true)}
                    {localConfig.columns === 3 && renderColumnEditor('center', 'Mitte', true)}
                    {localConfig.columns >= 2 && renderColumnEditor('right', 'Rechts', true)}
                  </div>
                </div>
              )}

              {/* Variable Info */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Tipp:</strong> Klicken Sie auf das Variable-Symbol {' '}
                <Variable className="h-4 w-4 inline" />, um automatisch befüllte Werte 
                wie Seitenzahl, Datum oder Vertragsname einzufügen.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
